import { useCallback, useEffect, useState } from "react"

import { useInputCapabilities } from "src/utils/input"

import "./TouchHints.css"

/* The other half of `title`.

   Forty-odd places in this app hang an explanation off a `title` attribute — why the daily claim is
   greyed out, what a bot's stance means, what a crown on a city costs you, a unit's stats inside a
   battle slot, the breakdown behind the food arrow, why your username cannot change yet. Every one
   of them is reached by resting a pointer somewhere, and a finger cannot rest anywhere. On a phone
   that information does not degrade — it is absent, with no sign that it was ever there.

   Fixing it site by site would be forty edits and forty chances to miss one. The shape they share
   is what makes one rule enough: **the load-bearing titles are the ones on something you cannot
   click**. A `<p>` with a number in it, a badge, a dot, a control that is disabled — for those, the
   title is the only door. On an *enabled* button the click already does the work and the title is a
   courtesy, so tapping one must do what it says and nothing else.

   So: no hover, tap something whose only affordance is its title, get the title. */

// Long enough to read a sentence, short enough that it does not sit over the map.
const HINT_MS = 4000
// A tap can start deep inside a badge; the title usually hangs a level or two up.
const SEARCH_DEPTH = 4

const INTERACTIVE = new Set(["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA", "SUMMARY"])

/* Interactive *and* able to act. A refused order is a button whose click does nothing, which is
   exactly when its title is the only thing left to explain it — the app marks those with
   `aria-disabled` rather than `disabled`, because a disabled button receives no events at all. */
export const isLiveControl = (element) => {
  if (!element) return false
  const interactive = INTERACTIVE.has(element.tagName) || element.getAttribute("role") === "button"
  if (!interactive) return false
  const refused =
    element.hasAttribute("disabled") ||
    element.getAttribute("aria-disabled") === "true" ||
    element.classList.contains("disabled")
  return !refused
}

/* The title worth showing for a tap, or null.

   Walks up from what was touched and takes the first title it finds — but only after satisfying
   itself that nothing in that chain is a live control. The order matters, and getting it wrong is
   how the first version behaved: the top bar's chips are `role="button"` wrappers that open a
   modal, with the title sitting on the `<p>` *inside* them. Returning as soon as a title appeared
   put a bubble on top of a modal that already explains the same thing, better. Tapping the food
   chip should open the food breakdown, not narrate it.

   So a title is the only door only when nothing around it is a door. */
export const hintFor = (target, depth = SEARCH_DEPTH) => {
  let found = null
  let element = target
  for (let step = 0; element && step <= depth; step += 1) {
    if (isLiveControl(element)) return null
    const title = element.getAttribute?.("title")
    if (!found && title && title.trim()) found = { text: title.trim(), element }
    element = element.parentElement
  }
  return found
}

/* Anchored under what was tapped, clamped inside the window. Kept in JS rather than CSS because it
   follows an arbitrary element rather than a layout slot. */
const placeUnder = (rect, width = 260) => {
  const margin = 8
  const left = Math.min(Math.max(margin, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - margin)
  // Below where it was tapped, unless that is off the bottom, in which case above.
  const below = rect.bottom + 10
  const flip = below + 90 > window.innerHeight
  return { left, top: flip ? Math.max(margin, rect.top - 10) : below, flip }
}

const TouchHints = () => {
  const { hover } = useInputCapabilities()
  const [hint, setHint] = useState(null)

  const dismiss = useCallback(() => setHint(null), [])

  useEffect(() => {
    // A pointer that can hover already has this, natively and better.
    if (hover) return undefined

    const onClick = (event) => {
      const found = hintFor(event.target)
      if (!found) {
        setHint(null)
        return
      }
      const rect = found.element.getBoundingClientRect()
      setHint({ text: found.text, ...placeUnder(rect) })
    }

    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  }, [hover])

  useEffect(() => {
    if (!hint) return undefined
    const timer = setTimeout(dismiss, HINT_MS)
    // Anything that moves the page moves the thing this was pointing at.
    window.addEventListener("scroll", dismiss, true)
    window.addEventListener("resize", dismiss)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("scroll", dismiss, true)
      window.removeEventListener("resize", dismiss)
    }
  }, [hint, dismiss])

  if (!hint) return null

  return (
    <div
      className={`touch-hint ${hint.flip ? "touch-hint-above" : ""}`}
      role="status"
      style={{ left: hint.left, top: hint.top }}
    >
      {hint.text}
    </div>
  )
}

export default TouchHints
